/*
 This file is part of GNU Taler
 (C) 2022-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  AmountJson,
  TalerCoreBankErrorsByMethod,
  TalerError,
  TranslatedString,
  TalerWithdrawUri,
} from "@gnu-taler/taler-util";
import { Loading, RouteDefinition, utils } from "@gnu-taler/web-util/browser";
import { VNode } from "preact";

import { Paytos } from "@gnu-taler/taler-util";
import { useComponentState, useWithdrawalOperationState } from "./state.js";
import { RetryableError } from "../../components/RetryableError.js";
import type { WithdrawalProgressMode } from "../WithdrawalProgress.js";
import {
  AbortedView,
  ConfirmedView,
  FailedView,
  InvalidPaytoView,
  InvalidReserveView,
  InvalidWithdrawalView,
  NeedConfirmationView,
  ReadyView,
} from "./views.js";

export interface Props {
  routeClose: RouteDefinition;
  onAbort: () => void;
  onContinueLater?: () => void;
  onOperationCreated?: (operationId: string) => void;
  focus?: boolean;
}

export interface WithdrawalOperationProps extends Props {
  operationId: string;
  clearWhenTerminal?: boolean;
}

export type State =
  | State.Loading
  | State.LoadingError
  | State.Ready
  | State.Failed
  | State.Aborted
  | State.Confirmed
  | State.InvalidPayto
  | State.InvalidWithdrawal
  | State.InvalidReserve
  | State.NeedConfirmation;

export namespace State {
  export interface Loading {
    status: "loading";
    error: undefined;
  }

  export interface Failed {
    status: "failed";
    error: TalerCoreBankErrorsByMethod<"createWithdrawal">;
  }

  export interface LoadingError {
    status: "loading-error";
    error: TalerError;
    title: TranslatedString;
    onRetry(): void;
  }

  /**
   * Need to open the wallet
   */
  export interface Ready {
    status: "ready";
    error: undefined;
    uri: TalerWithdrawUri;
    amount?:
      | { mode: "fixed"; value: AmountJson }
      | { mode: "suggested"; value: AmountJson };
    progressMode: WithdrawalProgressMode;
    focus?: boolean;
    onAbort: () => void;
    operationId: string;
    routeClose: RouteDefinition;
  }

  export interface InvalidPayto {
    status: "invalid-payto";
    error: undefined;
    payto: string | undefined;
  }
  export interface InvalidWithdrawal {
    status: "invalid-withdrawal";
    error: undefined;
    uri: string;
  }
  export interface InvalidReserve {
    status: "invalid-reserve";
    error: undefined;
    reserve: string | undefined;
  }
  export interface NeedConfirmation {
    status: "need-confirmation";

    account: string;
    onAbort: () => void;
    onContinueLater?: () => void;

    error: undefined;
    details: {
      account: Paytos.URI;
      reserve: string;
      username: string;
      amount:
        | { mode: "fixed"; value: AmountJson }
        | { mode: "bank-selected"; suggested?: AmountJson }
        | { mode: "missing" };
    };
    operationId: string;
    progressMode: WithdrawalProgressMode;
  }
  export interface Aborted {
    status: "aborted";
    error: undefined;
    routeClose: RouteDefinition;
  }
  export interface Confirmed {
    status: "confirmed";
    error: undefined;
    routeClose: RouteDefinition;
  }
}

const viewMapping: utils.StateViewMap<State> = {
  loading: Loading,
  failed: FailedView,
  "invalid-payto": InvalidPaytoView,
  "invalid-withdrawal": InvalidWithdrawalView,
  "invalid-reserve": InvalidReserveView,
  "need-confirmation": NeedConfirmationView,
  aborted: AbortedView,
  confirmed: ConfirmedView,
  "loading-error": RetryableError,
  ready: ReadyView,
};

export const OperationState: (p: Props) => VNode = utils.compose(
  (p: Props) => useComponentState(p),
  viewMapping,
);

export const WithdrawalOperation: (p: WithdrawalOperationProps) => VNode =
  utils.compose(
    (p: WithdrawalOperationProps) => useWithdrawalOperationState(p),
    viewMapping,
  );
